import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { getProduct, products } from '../data/products';
import { getCategory } from '../data/categories';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/shop/ProductCard';
import { CtaBand } from '../components/ui/CtaBand';
import { FaqList } from '../components/ui/FaqList';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProduct(slug) : undefined;
  const {
    brand,
    text,
    productImage
  } = useBrand();
  useDocumentMeta({
    title: product ? `${product.name} — ${brand.name}` : `Shop — ${brand.name}`,
    description: product ? `${product.tagline} ${product.price}${product.cadence} — ${product.priceNote}.` : 'Doctor-prescribed treatments with the price in the open.',
    image: product ? productImage(product.slug, product.image) : brand.images.hero
  });
  if (!product) {
    return <main className="bg-cream" data-id="element-545" data-mp="3avuiw47v5pk4">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-546" data-mp="3awx724dsxdwc">
          <h1 className="max-w-2xl font-serif text-4xl font-semibold leading-tight text-ink md:text-5xl" data-id="element-547" data-mp="3fuyrasmyrj9e">
            That page has moved on.
          </h1>
          <Button to="/shop" className="mt-8" data-id="element-548" data-mp="7fnchpy2de7n">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-549" data-mp="bgh00i7lgr00" />
            Back to the shop
          </Button>
        </div>
      </main>;
  }
  const category = getCategory(product.category);
  const related = product.pairsWith.map(pair => products.find(item => item.slug === pair)).filter((item): item is NonNullable<typeof item> => Boolean(item));
  return <main data-id="element-550" data-mp="2i5vnlr32sw70">
      <section className="bg-cream" data-id="element-551" data-mp="2i9jiatk9bonb">
        <div className="mx-auto max-w-shell px-6 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12" data-id="element-552" data-mp="2n6t6k5vwtu9k">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm" data-id="element-553" data-mp="233x6lthtm45r">
            <Link to="/shop" className="text-ink-muted transition-colors hover:text-ink" data-id="element-554" data-mp="1z2tv1j6t2uac">
              Shop
            </Link>
            <span className="text-ink-muted" data-id="element-555" data-mp="1uatov905755b">/</span>
            <Link to={`/categories/${product.category}`} className="text-ink-muted transition-colors hover:text-ink" data-id="element-556" data-mp="1u9in1eq9c7gu">
              {category?.name}
            </Link>
            <span className="text-ink-muted" data-id="element-557" data-mp="1fektiijdhby6">/</span>
            <span className="text-ink" data-id="element-558" data-mp="1fd1a4j2f6ks6">{product.name}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-559" data-mp="1bc7oxxgd3k2z">
            <div className="overflow-hidden rounded-brand bg-cream-deep" data-id="element-560" data-mp="16e62r3nn34na">
              <img src={productImage(product.slug, product.image)} alt={`${product.name}, ${product.form}`} className="aspect-square w-full object-cover" data-id="element-561" data-mp="16fzegxd40f25" />
            </div>

            <div data-id="element-562" data-mp="38byhd782nky2">
              <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-563" data-mp="38af0beeld5v8">
                {product.status}
              </p>
              <h1 className="mt-3 font-serif text-4xl font-semibold leading-[1.05] text-ink md:text-5xl" data-id="element-564" data-mp="3d8orzf13xoc3">
                {product.name}
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-id="element-565" data-mp="3n0m8906z36ho">{product.tagline}</p>

              <dl className="rule mt-8 grid grid-cols-2 gap-6 pt-6 sm:grid-cols-3" data-id="element-566" data-mp="2pddrfihx2lm0">
                <div data-id="element-567" data-mp="2ubf85wrve8ub">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-568" data-mp="2ucqd3zx1xmn0">Form</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-569" data-mp="2zaifjnvbor5g">{product.form}</dd>
                </div>
                <div data-id="element-570" data-mp="33clu4ldagbuy">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-571" data-mp="33d4z71hub5rk">Strength</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-572" data-mp="ejreqbc2eyg9">{product.strength}</dd>
                </div>
                <div data-id="element-573" data-mp="iktgozxd9olx">
                  <dt className="text-xs uppercase tracking-label text-ink-muted" data-id="element-574" data-mp="ikbd1i8dk83z">Route</dt>
                  <dd className="mt-2 text-sm text-ink" data-id="element-575" data-mp="h5h8b0886cb9">{product.route}</dd>
                </div>
              </dl>

              <div className="mt-8 rounded-brand border border-ink/10 bg-cream-light p-6" data-id="element-576" data-mp="c7z1pghok96f">
                <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-577" data-mp="r4g77r36utl2">
                  <div data-id="element-578" data-mp="m2h5smr0e6qr">
                    <p className="font-serif text-4xl font-semibold leading-none text-ink" data-id="element-579" data-mp="m651nsjxf58n">
                      ${product.price}
                      <span className="ml-1 text-base font-normal text-ink-muted" data-id="element-580" data-mp="v59y8tbxgoi6">
                        {product.cadence}
                      </span>
                    </p>
                    <p className="mt-2 text-sm text-ink-muted" data-id="element-581" data-mp="19vha2q7fj99w">{product.priceNote}</p>
                  </div>
                  <p className="text-sm text-ink-muted" data-id="element-582" data-mp="1iub2e4dq9dhn">
                    Plus $39/mo membership
                    <span className="block text-ink" data-id="element-583" data-mp="1eszimfhjbmhd">Same price for a year</span>
                  </p>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-584" data-mp="1xgrwt0i3lpne">
                  <Button to={`/get-started?product=${product.slug}`} size="lg" className="flex-1" data-id="element-585" data-mp="22g4kfngsn0r1">
                    Ask about this
                    <ArrowRightIcon className="h-4 w-4" data-id="element-586" data-mp="1ofa84tqmkfn3" />
                  </Button>
                  <Button to="/how-it-works" variant="secondary" size="lg" data-id="element-587" data-mp="1ten4c53dyz2c">
                    How it works
                  </Button>
                </div>

                <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-588" data-mp="1eofmnrl7dk2p">
                  <li className="flex items-center gap-2 text-xs text-ink-muted" data-id="element-589" data-mp="1ji0jhgb2880b">
                    <ShieldCheckIcon className="h-4 w-4 shrink-0 text-brick" strokeWidth={1.75} data-id="element-590" data-mp="15qn5ehvx82co" />
                    Prescription only. Nothing charged until you say yes.
                  </li>
                  <li className="flex items-center gap-2 text-xs text-ink-muted" data-id="element-591" data-mp="vredrvoj2zth">
                    <TruckIcon className="h-4 w-4 shrink-0 text-brick" strokeWidth={1.75} data-id="element-592" data-mp="10qhfp797opx8" />
                    Free shipping in a plain box, cold-packed when it needs to be
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-593" data-mp="2tnd1ujshbsht">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-594" data-mp="2to4nb1sd49ud">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-595" data-mp="2opbalbkwgmv9">
            <div data-id="element-596" data-mp="3leiu6e1yklwj">
              <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl" data-id="element-597" data-mp="3lf2ge6yn5wgd">
                About {product.name}
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-soft" data-id="element-598" data-mp="3hdz23ow8s8h9">
                {text(product.description)}
              </p>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-599" data-mp="37fj8nf9qsn4l">
                {product.highlights.map(highlight => <li key={highlight} className="flex items-start gap-3" data-id="element-600" data-mp="20zz10alnucsf">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2.25} data-id="element-601" data-mp="1w04e0i50a7cn" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-602" data-mp="1lw5yawhb7om1">{highlight}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-603" data-mp="1qso31wcn3myd">
              <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-604" data-mp="1vudfngfig8ok">
                The details
              </h2>
              <dl className="mt-5 rounded-brand border border-ink/10 bg-cream px-6" data-id="element-605" data-mp="20lvenyxz3z6w">
                {product.facts.map(fact => <div key={fact.label} className="flex items-baseline justify-between gap-6 border-b border-ink/10 py-4 last:border-b-0" data-id="element-606" data-mp="24oi2loriwzyt">
                    <dt className="text-sm text-ink-muted" data-id="element-607" data-mp="2eiidy8def1fz">{fact.label}</dt>
                    <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-608" data-mp="2jj63eplzba83">{fact.value}</dd>
                  </div>)}
                <div className="flex items-baseline justify-between gap-6 border-t border-ink/10 py-4" data-id="element-609" data-mp="k8wx7dih3ybe">
                  <dt className="text-sm text-ink-muted" data-id="element-610" data-mp="2r379jwztjcp0">Made by</dt>
                  <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-611" data-mp="2r1cnuggq3gjv">{product.status}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {product.faq && product.faq.length > 0 && <section className="bg-cream" data-id="element-612" data-mp="2vu5oot92m4qo">
          <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-613" data-mp="2vufjqv8z3238">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-614" data-mp="283jmvjpntgp4">
              <h2 className="font-serif text-3xl font-bold leading-tight tracking-tight text-ink md:text-4xl" data-id="element-615" data-mp="235i4l1025lep">
                Before you start
              </h2>
              <FaqList items={product.faq} data-id="element-616" data-mp="2h2wue6u83v77" />
            </div>
          </div>
        </section>}

      {related.length > 0 && <section className="bg-cream-light" data-id="element-617" data-mp="3sjwp1941mm4e">
          <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-618" data-mp="3siljp4uy96b6">
            <div className="rule flex flex-wrap items-baseline justify-between gap-3 pt-6" data-id="element-619" data-mp="3ohji4hdk8i61">
              <h2 className="font-serif text-2xl font-semibold text-ink" data-id="element-620" data-mp="3w1ucb6svwhq0">
                People usually run this alongside
              </h2>
              <Link to={`/categories/${product.category}`} className="text-sm text-brick transition-colors hover:text-ink" data-id="element-621" data-mp="4tpz5fuhs4ja">
                All {category?.name}
              </Link>
            </div>
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-622" data-mp="imo33ql034yg">
              {related.map(item => <ProductCard key={item.slug} product={item} data-id="element-623" data-mp="nl925moinilb" />)}
            </ul>
          </div>
        </section>}

      <CtaBand data-id="element-624" data-mp="8vbklpl3dr53" />
    </main>;
}